import type { ReactElement } from "react"
import { useContext } from "react"

import { useMutation, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import * as Yup from "yup"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"

import { LoadingContainer } from "../components/core/LoadingContainer"
import {
  AutoMLConfigContainer,
  ClassificationModelConfig,
  Context,
  NEGATIVE_SLIDER_DEFAULT_VALUE,
  POSITIVE_SLIDER_DEFAULT_VALUE,
} from "../features/ModelConfigurations"
import { SINGLE_SLIDER_DEFAULT_VALUE } from "../features/ModelConfigurations/Constants"
import { setCurrentModel } from "../features/Models"
import { setCurrentProject } from "../features/projects/Slice"
import { ModelsLayout } from "../layouts/ModelsLayout"
import { KonanAPI } from "../services/KonanAPI"
import { queryClient } from "../store/QueryClientStore"
import type { RootState } from "../store/ReduxStore"
import type { ConfigureAutoMlModelRequest, baseErrorType } from "../types/custom/projects"
import type { Deployment } from "../types/generated/api/Deployment"
import type { Label } from "../types/generated/api/Label"
import type { Model } from "../types/generated/api/Model"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"
import type { PaginatedModelSettingsSetList } from "../types/generated/api/PaginatedModelSettingsSetList"

import styles from "./ModelConfigurations.module.scss"

// TODO:: refactor later
const configValidationSchema = Yup.object({
  positiveLabel: Yup.object({
    name: Yup.string().required("Required"),
  }),
  negativeLabel: Yup.object({
    name: Yup.string().required("Required"),
  }),
  singleThreshold: Yup.number().required("Required"),
  positiveThreshold: Yup.number().required("Required"),
  negativeThreshold: Yup.number().required("Required"),
})

/**
 * The ModelConfigurations component is the main page for configuring a model. It is responsible for
 * rendering the correct configuration form based on the model type.
 *
 * @returns {ReactElement}
 */
export const ModelConfigurations = (): ReactElement => {
  const { id: projectId } = useParams<{ id: string }>()

  const [searchParams, setSearchParams] = useSearchParams()

  const navigate = useNavigate()

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const {
    setLatestVersionNum,
    setSelectedVersionNum,
    setConfigHistoryList,
    setCurrentConfigHistory,
    currentConfigHistory,
    thresholdMetricsData,
    isSidebarOpen,
  } = useContext(Context)

  const dispatch = useDispatch()

  // Fetching project
  const { isFetching: isProjectsFetching } = useQuery<AxiosResponse<Deployment>, AxiosError>(
    ["project", projectId],
    () => KonanAPI.fetchProject(projectId as string),
    {
      onSuccess: (response) => {
        if (currentProject?.uuid !== response?.data?.uuid) {
          dispatch(setCurrentProject(response?.data))
        }
      },
      enabled: !!projectId,
    },
  )

  // Fetch all models to get the first model id if no searchParams is found
  const { isFetching: isModelsFetching } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", projectId, "state"],
    () => KonanAPI.fetchModels({ projectId: projectId as string, ordering: "state" }),
    {
      onSuccess: ({ data }) => {
        setSearchParams((prev) => {
          prev.set("model_id", data.results?.[0] ? data.results[0].uuid : "")
          return prev
        })
      },
      enabled: !!projectId && !searchParams.get("model_id") && !currentModel,
    },
  )

  // Fetch the model data to process it and render it
  const {
    data: autoMLRes,
    isFetching: isModelDataFetching,
    isError: isAutoMLConfigError,
    refetch,
  } = useQuery<AxiosResponse<Model>, AxiosError>({
    queryKey: ["model_data", searchParams.get("model_id")],
    queryFn: () => KonanAPI.fetchModel(searchParams.get("model_id") as string),
    onSuccess: ({ data }) => {
      dispatch(setCurrentModel(data))
    },
    enabled: !!projectId && !!searchParams.get("model_id"),
  })

  // Fetch configuration History list
  const { isFetching: isConfigHistoryLoading } = useQuery<
    AxiosResponse<PaginatedModelSettingsSetList>,
    AxiosError<baseErrorType>
  >({
    queryKey: ["model-config", searchParams.get("model_id")],
    queryFn: () =>
      KonanAPI.fetchModelConfigurationsList((searchParams.get("model_id") as string) || (currentModel?.uuid as string)),
    onSuccess: ({ data }) => {
      setLatestVersionNum(data?.count || 0)
      setSelectedVersionNum(data.count)
      setConfigHistoryList(data.results)
      setCurrentConfigHistory(data?.results?.[0])
    },
    enabled: !!currentModel && !!searchParams.get("model_id"),
  })

  // Handle sending the configuration mutation to the BE
  const configureAutoMlModeMutation = useMutation<AxiosResponse, AxiosError, ConfigureAutoMlModelRequest>(
    KonanAPI.configureAutoMlModel,
    {
      mutationKey: "configure-automl",
      onSuccess: async () => {
        NotificationUtils.toast("Model configured successfully", { severity: "success" })

        formik?.resetForm()
        queryClient.invalidateQueries("model-configurations")
        navigate(`/projects/${projectId}/decision-engines?model_id=${currentModel?.uuid}`)
      },
      onError: () => {
        NotificationUtils.toast("An error occurred while configuring model", { severity: "error" })
      },
    },
  )

  const createLabelsMutation = useMutation<
    AxiosResponse<Label[]>,
    AxiosError,
    {
      project_uuid: string
      labelsNames: { name: string }[]
    }
  >(KonanAPI.BulkCreateLabels, {
    onError: () => {
      NotificationUtils.toast("Label creation failed", { severity: "error" })
    },
  })

  // Initial form values for the metrics data
  const formik = useFormik({
    validationSchema: configValidationSchema,
    initialValues: {
      labels: [],
      positiveLabel: { name: "" },
      negativeLabel: { name: "" },
      singleThreshold: SINGLE_SLIDER_DEFAULT_VALUE,
      positiveThreshold: POSITIVE_SLIDER_DEFAULT_VALUE,
      negativeThreshold: NEGATIVE_SLIDER_DEFAULT_VALUE,
    },
    onSubmit: async ({ singleThreshold, negativeThreshold, positiveThreshold, positiveLabel, negativeLabel }) => {
      const labels = await createLabelsMutation.mutateAsync({
        project_uuid: projectId as string,
        labelsNames: [positiveLabel, negativeLabel],
      })

      await configureAutoMlModeMutation.mutateAsync({
        projectId: projectId || "",
        modelUUID: currentModel?.uuid || "",
        lower_bound: thresholdMetricsData?.type === "V2" ? negativeThreshold : singleThreshold,
        upper_bound: thresholdMetricsData?.type === "V2" ? positiveThreshold : singleThreshold,
        positive_label: labels.data.find((label) => label.name === positiveLabel.name)?.uuid as string,
        negative_label: labels.data.find((label) => label.name === negativeLabel.name)?.uuid as string,
        state: currentModel?.state === "live" ? currentModel.state : "live",
      })
    },
  })

  if (isProjectsFetching || isModelsFetching || isModelDataFetching || isConfigHistoryLoading)
    return (
      <div style={{ height: "calc(100dvh - 242px)" }}>
        <LoadingContainer />
      </div>
    )

  // handle error when fetching model configurations or when there is no threshold metrics
  // or metrics data is returned and the model is autoML
  if (
    isAutoMLConfigError ||
    !autoMLRes?.data.type ||
    (autoMLRes?.data?.type === "automl" &&
      (!autoMLRes?.data?.threshold_metrics || (!autoMLRes?.data?.metrics && thresholdMetricsData?.type === "V2")))
  ) {
    return (
      <div className={styles.errorContainer}>
        <Typography variant="h2-bold" gutterBottom>
          Couldn't Fetch Data, please Try again!
        </Typography>
        <Button variant="primary" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <FormikProvider value={formik}>
      <ModelsLayout sidebarVariant="config" isSidebarOpen={isSidebarOpen}>
        {currentProject?.type === "credit_scoring" && (
          <AutoMLConfigContainer currentHistoryData={currentConfigHistory} />
        )}
        {currentProject?.type === "classification" && (
          <ClassificationModelConfig mode={!!currentConfigHistory ? "view" : "edit"} />
        )}
      </ModelsLayout>
    </FormikProvider>
  )
}
